import { numberIsPrintable } from './formatNumber.js';
const DEFAULT_LOCALE = 'en-us'; //Locale Service is Not Implemented Yet - Set the locale to en-us as default for now
const NUMERIC = 'numeric';
const UTC_TIMEZONE = 'UTC';
const TWO_DIGIT = '2-digit';
/**
 * Value to use when no data provided, is null, undefined or invalid
 */
export const NO_VALUE = 'N/A';
/**
 * Formats the date to the provided format
 * If the parameters provided are not valid it returns 'N/A'
 */
export function customFormatDate(dateAsString, formatOptions, locale, customNoValueSymbol) {
    if (dateAsString && formatOptions) {
        const date = new Date(dateAsString);
        if (!isNaN(date.getTime())) {
            return new Intl.DateTimeFormat(locale ?? DEFAULT_LOCALE, {
                ...formatOptions,
                timeZone: UTC_TIMEZONE,
            }).format(date);
        }
    }
    return customNoValueSymbol ?? NO_VALUE;
}
/**
 * Formats a date string into a short date format D/M/YYYY - with no leading zeroes.
 * If the date provided is not valid, it returns N/A *
 */
export function dateShort(dateAsString) {
    return customFormatDate(dateAsString, {
        year: NUMERIC,
        month: NUMERIC,
        day: NUMERIC,
    });
}
/**
 * Converts a date string into a two-digit format for month and day MM/DD/YYYY - with leading zeroes.
 * If the date provided is not valid, it returns a default value.
 */
export function dateToMMDDYYYY(dateAsString) {
    return customFormatDate(dateAsString, {
        year: NUMERIC,
        month: TWO_DIGIT,
        day: TWO_DIGIT,
    });
}
/**
 * Converts a date string into a YYYYMMDD format.
 * If the date provided is not valid, it returns an empty string.
 */
export function dateToYYYYMMDD(dateAsString) {
    if (dateAsString) {
        const date = new Date(dateAsString);
        if (!isNaN(date.getTime())) {
            const parts = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
                year: NUMERIC,
                month: TWO_DIGIT,
                day: TWO_DIGIT,
                timeZone: UTC_TIMEZONE,
            }).formatToParts(date);
            const [year, month, day] = parts.reduce((acc, part) => {
                if (part.type === 'year') {
                    acc[0] = part.value;
                }
                if (part.type === 'month') {
                    acc[1] = part.value;
                }
                if (part.type === 'day') {
                    acc[2] = part.value;
                }
                return acc;
            }, ['', '', '']);
            return `${year}${month}${day}`;
        }
    }
    return '';
}
/**
 * Given a date in the form of a string, the function returns the YYYY-MM-DD format
 */
export function dateStringSplit(dateAsString, splitchar = 'T') {
    if (dateAsString) {
        const date = new Date(dateAsString);
        if (!isNaN(date.getTime())) {
            return date.toISOString().split(splitchar)[0];
        }
    }
    return NO_VALUE;
}
/**
 * Given a date in the form of a string, the function returns the date's year
 * If the date provided is not valid, it returns N/A
 */
export function getYearFromDate(dateAsString) {
    if (dateAsString) {
        const date = new Date(dateAsString);
        if (!isNaN(date.getTime())) {
            return new Intl.DateTimeFormat(DEFAULT_LOCALE, {
                year: NUMERIC,
                timeZone: UTC_TIMEZONE,
            }).format(date);
        }
    }
    return NO_VALUE;
}
/**
 * Given a number, function converts the number to a date
 * The number should represent a date in UTC
 * Converts the generated date to a string that the dateShort function can handle
 * Returns N/A if parameter is null or undefined
 */
export function dateShortFromMilliseconds(dateValueAsNumber) {
    if (typeof dateValueAsNumber === 'number' &&
        numberIsPrintable(dateValueAsNumber)) {
        const dateFromNumber = new Date(dateValueAsNumber);
        return dateShort(dateFromNumber.toISOString());
    }
    return NO_VALUE;
}
/**
 * Checks if a given date is the last day of a quarter.
 * @param date - The date to check in the format 'YYYY-MM-DD'.
 * @returns True if the date is the last day of a quarter, false otherwise.
 */
export function isQuarterEndDate(date) {
    const [year, month, day] = date.split('-').map(Number);
    const parsedDate = new Date(Date.UTC(year, month - 1, day));
    const monthIndex = parsedDate.getUTCMonth() + 1;
    const isLastDayOfMonth = (d) => {
        return (d.getUTCDate() ===
            new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate());
    };
    return ((monthIndex === 3 ||
        monthIndex === 6 ||
        monthIndex === 9 ||
        monthIndex === 12) &&
        isLastDayOfMonth(parsedDate));
}
//# sourceMappingURL=formatDate.js.map